import React, { useState, useEffect, useRef } from "react";
import { useHistory } from 'react-router';
import Style from './Security.module.css';
import Button from '../../../components/controls/button/Button'
import Input from '../../../components/controls/input/customInput/CustomInput'
import Checkmark from '../../../assets/svg/CheckmarkGray.svg';
import CheckmarkGreen from '../../../assets/svg/Checkmark.svg';
import Dismiss from '../../../assets/svg/Dismiss.svg';
import { mfaService } from '../../../store/services/mfa.service';
import PinInput from "react-pin-input";
import Skeleton from "react-loading-skeleton";
import { generalActions } from "../../../store/actions";
import { useDispatch } from "react-redux";
import { QRCodeSVG } from 'qrcode.react';
import { Check, Lock } from "lucide-react";
import { clearLocalStorage } from "../../../utils/localStorageUtils";
const Security = (props) => {
    const { resetPasswordRequest, loading, error } = props;
    const [currentPassword, setcurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [disabled, setDisabled] = useState(true);
    const [validationResults, setValidationResults] = useState({
        length: false,
        mixLettersNumbers: false,
        specialCharacter: false,
        mixCase: false,
    });
    const [formErrors, setFormErrors] = useState({
        password: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [showDisableMFa, setShowDisableMFa] = useState(false);
    const [showTimedCheckMark, setShowTimedCheckMark] = useState(false);
    const [mfaStatus, setMfaStatus] = useState({ enabled: false, enforced: false, loading: true });
    const [mfaEnrollment, setMfaEnrollment] = useState({
        step: 'initial', // 'initial', 'enrolling', 'completed', 'finished'
        qrCodeUri: '',
        manualEntryKey: '',
        backupCodes: [],
        loading: false,
        error: ''
    });
    const [mfaCode, setMfaCode] = useState('');
    const [mfaError, setMfaError] = useState('');
    const [isValidCode, setIsValidCode] = useState(false);
    const [isValidEnrollCode, setIsValidEnrollCode] = useState(false);
    const pinCodeRef = useRef();
    const enrollPinCodeRef = useRef();
    const dispatch = useDispatch();
    const history = useHistory();

    const handleUpdatePassword = async () => {
        const errors = {};
        if (!currentPassword) errors['password'] = `The Password field is required.`;
        if (!password) errors['newPassword'] = `The NewPassword field is required.`;
        if (!confirmPassword) errors['confirmPassword'] = `The ConfirmPassword field is required.`;
        setFormErrors(errors);
        if (Object.keys(errors).length) {
            return;
        }
        if (password !== confirmPassword) {
            setPasswordError("Passwords don't match");
        } else {
            setPasswordError('');

            resetPasswordRequest({
                password: currentPassword,
                newPassword: password,
                ConfirmPassword: confirmPassword,
            });
        }
    };

    useEffect(() => {
        if (
            validationResults.length &&
            validationResults.mixLettersNumbers &&
            validationResults.specialCharacter &&
            validationResults.mixCase &&
            currentPassword.length > 0
        ) {
            setDisabled(false);
        }
    }, [validationResults, currentPassword, password, confirmPassword]);

    const activateTimedCheckMark = () => {
        setShowTimedCheckMark(true);
        setTimeout(() => {
            setShowTimedCheckMark(false);
        }, 500);
    }

    const handleChange = (e) => {
        setPassword(e);
        const lengthRegex = /.{8,}/;
        const mixLettersNumbersRegex = /(?=.*[A-Za-z])(?=.*\d)/;
        const specialCharacterRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
        const mixCaseRegex = /(?=.*[a-z])(?=.*[A-Z])/;

        setValidationResults({
            length: lengthRegex.test(e),
            mixLettersNumbers: mixLettersNumbersRegex.test(e),
            specialCharacter: specialCharacterRegex.test(e),
            mixCase: mixCaseRegex.test(e),
        });
    };

    const handleForgot = () => {
        clearLocalStorage();
        history.push('/forgot-password');
    }

    const loadMfaStatus = async () => {
        setMfaStatus({ ...mfaStatus, loading: true });
        const result = await mfaService.getStatus();
        if (result.success) {
            setMfaStatus({
                enabled: result.data.isEnabled,
                enforced: result.data.isEnforced,
                loading: false
            });
        } else {
            setMfaStatus({
                enabled: false,
                enforced: false,
                loading: false
            });
        }
    };

    const handleMfaEnroll = async () => {
        setMfaEnrollment({ ...mfaEnrollment, loading: true, error: '' });
        const result = await mfaService.enroll();
        if (result.success) {
            setMfaEnrollment({
                ...mfaEnrollment,
                step: 'enrolling',
                qrCodeUri: result.data.qrCodeUri,
                manualEntryKey: result.data.manualEntryKey,
                loading: false
            });
        } else {
            setMfaEnrollment({
                ...mfaEnrollment,
                loading: false,
                error: result.error
            });
        }
    };

    const markEnrollementStepAsFinished = () => {
        setShowDisableMFa(false);
        setIsValidCode(false);
        setMfaEnrollment({ ...mfaEnrollment, step: 'finished' });
    }
    const handleMfaVerify = async () => {
        setMfaEnrollment({ ...mfaEnrollment, loading: true });
        const result = await mfaService.validate(mfaCode);
        if (result.success) {
            setMfaEnrollment({
                ...mfaEnrollment,
                step: 'completed',
                backupCodes: result.data.backupCodes,
                loading: false
            });
            setMfaStatus({ enabled: true, enforced: result.data.isEnforced, loading: false });
            setMfaCode('');
            setMfaError('');
            setIsValidEnrollCode(false);
            dispatch(
                generalActions.showNotificationToast({
                    title: "MFA has been successfully enabled for your account",
                    type: "success",
                })
            );
        } else {
            setMfaEnrollment({ ...mfaEnrollment, loading: false });
            setMfaError(result.error);
            setMfaCode('');
            setIsValidEnrollCode(false);
            enrollPinCodeRef?.current?.clear();
            enrollPinCodeRef?.current?.focus();
        }
    };

    const handleMfaDisable = async () => {
        if(mfaStatus.enforced)
            return;
        
        setMfaEnrollment({ ...mfaEnrollment, loading: true });
        const result = await mfaService.disable(mfaCode);
        if (result.success) {
            setMfaStatus({ enabled: false, loading: false });
            setMfaEnrollment({
                step: 'initial',
                qrCodeUri: '',
                manualEntryKey: '',
                backupCodes: [],
                loading: false,
                error: ''
            });
            setMfaCode('');
            setMfaError('');
            dispatch(
                generalActions.showNotificationToast({
                    title: "MFA has been successfully disabled for your account",
                    type: "success",
                })
            );
        } else {
            setMfaEnrollment({ ...mfaEnrollment, loading: false });
            setMfaError(result.error);
            setMfaCode('');
            pinCodeRef?.current?.clear();
            pinCodeRef?.current?.focus();
        }
    };

    const handleCancelEnrollment = () => {
        setMfaEnrollment({
            step: 'initial',
            qrCodeUri: '',
            manualEntryKey: '',
            backupCodes: [],
            loading: false,
            error: ''
        });
        setMfaCode('');
        setMfaError('');
        setIsValidEnrollCode(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    useEffect(() => {
        if (error !== "") {
            setFormErrors({});
            Object.keys(error).forEach((key) => {
                if (key === 'Password') {
                    setFormErrors((prevErrors) => ({
                        ...prevErrors,
                        password: error[key][0]
                    }));
                }
                if (key === 'NewPassword') {
                    setFormErrors((prevErrors) => ({
                        ...prevErrors,
                        newPassword: error[key][0]
                    }));
                }
                if (key === 'ConfirmPassword') {
                    setFormErrors((prevErrors) => ({
                        ...prevErrors,
                        confirmPassword: error[key][0]
                    }));
                }
                if (key === 'code') {
                    console.log('meetings...', error);
                    setFormErrors((prevErrors) => ({
                        ...prevErrors,
                        password: error.message
                    }));
                }
            });
        } else {
            setFormErrors({});
        }
    }, [error])

    useEffect(() => {
        setFormErrors({});
        loadMfaStatus();
    }, []);

    const handleCodeChanged = (code) => {
        const isValid = /^\d{6}$/.test(code);
        setIsValidCode(isValid);
    }

    const handleEnrollCodeChanged = (code) => {
        const isValid = /^\d{6}$/.test(code);
        setIsValidEnrollCode(isValid);
    }

    return (
        <div className={`${Style.mainWrapper} font-family-imp`}>
            <p className={Style.infoLabel}>Security</p>
            <div className={Style.mainContainer}>
                <div className="flex w-100 align-items-baseline gap-2">
                    <p className={Style.label}>Multi-factor authentication (MFA)</p>
                    {mfaStatus.enabled && <span class="pill purple">Enabled</span>}
                </div>
                <div className="w-100">
                    {mfaStatus.loading ? (
                        <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                    ) : (
                        <>
                            {!mfaStatus.enabled || mfaEnrollment.step === 'completed' ? (
                                <div className={Style.mfaDisabled}>
                                    {mfaEnrollment.step === 'initial' && (
                                        <>
                                            <p className={Style.mfaDescription}>
                                                Secure your account with two-factor authentication. You'll need to enter a code from your authenticator app each time you sign in.
                                            </p>
                                            <Button
                                                btnClass="d_btn d_btn_primary text-sm-400 font-family-imp"
                                                cssClass={Style.btnMfa}
                                                label="Enable MFA"
                                                showLoading={mfaEnrollment.loading}
                                                clickHandler={handleMfaEnroll}
                                            />
                                            {mfaEnrollment.error && (
                                                <p className={Style.errorMessage}>{mfaEnrollment.error}</p>
                                            )}
                                        </>
                                    )}

                                    {mfaEnrollment.step === 'enrolling' && (
                                        <div className={Style.mfaEnrollment}>
                                            <p className={Style.mfaStepTitle}>Step 1: Scan QR Code</p>
                                            <p className={Style.mfaStepDescription}>
                                                Scan this QR code with your authenticator app (Microsoft Authenticator, Authy, etc.)
                                            </p>
                                            <div className={Style.qrCodeContainer}>
                                                <QRCodeSVG
                                                    value={mfaEnrollment.qrCodeUri}
                                                    size={200}
                                                    bgColor="#ffffff"
                                                    fgColor="#000000"
                                                    level="M"
                                                    imageSettings={{
                                                        src: "https://ppmg-dev.s3.ca-central-1.amazonaws.com/logos/default.svg",
                                                        x: undefined,
                                                        y: undefined,
                                                        height: 24,
                                                        width: 120,
                                                        opacity: 1,
                                                        excavate: true,
                                                    }}
                                                />
                                            </div>
                                            <p className={Style.mfaStepTitle}>Or enter this key manually:</p>
                                            <div className={Style.manualKeyContainer}>
                                                <span className={Style.manualKey}>{mfaEnrollment.manualEntryKey}</span>
                                                <Button
                                                    btnClass="d_btn d_btn_secondary text-sm-400 font-family-imp"
                                                    cssClass={Style.btnCopy}
                                                    label="Copy"
                                                    icon={showTimedCheckMark ? <Check size={16} color="#333" /> : null}
                                                    clickHandler={() => {
                                                        copyToClipboard(mfaEnrollment.manualEntryKey)
                                                        activateTimedCheckMark();
                                                    }}
                                                />
                                            </div>
                                            <div className={Style.mfaVerificationSection}>
                                                <p className={Style.mfaStepTitle}>Step 2: Enter Verification Code</p>
                                                <div className={`${Style.codeFieldWrapper}`}>
                                                    <PinInput
                                                        ref={enrollPinCodeRef}
                                                        length={6}
                                                        type="numeric"
                                                        inputMode="number"
                                                        inputStyle={{ borderColor: '#808D9E', borderRadius: '4px', color: '#342c82' }}
                                                        inputFocusStyle={{ borderColor: '#342c82' }}
                                                        autoSelect={true}
                                                        focus={true}
                                                        onComplete={(val, idx) => { setMfaCode(val) }}
                                                        onChange={(val, idx) => { handleEnrollCodeChanged(val) }}
                                                    />
                                                    {isValidEnrollCode && (
                                                        <Button
                                                            btnClass="d_btn d_btn_primary text-sm-400 font-family-imp"
                                                            cssClass={Style.btnMfa}
                                                            label="Verify & Enable"
                                                            showLoading={mfaEnrollment.loading}
                                                            isDisabled={mfaEnrollment.loading || !isValidEnrollCode}
                                                            clickHandler={handleMfaVerify}
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex justify-content-center align-items-center">
                                                    {mfaError && <p className={Style.errorMessage}>{mfaError}</p>}
                                                </div>
                                                <div className={Style.mfaButtonGroup}>
                                                    <Button
                                                        btnClass="d_btn d_btn_secondary text-sm-400 font-family-imp border border-[#d1d1d1] bg-white text-[#242424] p-1"
                                                        cssClass={Style.btnMfa}
                                                        label="Cancel"
                                                        clickHandler={handleCancelEnrollment}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {mfaEnrollment.step === 'completed' && (
                                        <div className={Style.mfaSuccess}>
                                            <div className={Style.successMessage}>
                                                <Check size={18} color="#107c10" />
                                                <p>MFA enabled successfully</p>
                                            </div>
                                            <div className={Style.backupCodesSection}>
                                                <p className={Style.mfaStepTitle}>Save Your Backup Codes</p>
                                                <p className={Style.backupCodesDescription}>
                                                    Store these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
                                                </p>
                                                <div className={Style.backupCodesContainer}>
                                                    {mfaEnrollment.backupCodes.map((code, index) => (
                                                        <span key={index} className={Style.backupCode}>{code}</span>
                                                    ))}
                                                </div>
                                                <div className="flex gap-2 justify-content-center align-items-center">
                                                    <Button
                                                        btnClass="d_btn d_btn_primary text-sm-400 font-family-imp"
                                                        cssClass={Style.btnMfa}
                                                        label="Copy All Codes"
                                                        icon={showTimedCheckMark ? <Check size={16} color="#fff" /> : null}
                                                        clickHandler={() => {
                                                            copyToClipboard(mfaEnrollment.backupCodes.join('\n'));
                                                            activateTimedCheckMark();
                                                        }}
                                                    />
                                                    <Button
                                                        btnClass="d_btn d_btn_primary text-sm-400 font-family-imp"
                                                        cssClass={Style.btnMfa}
                                                        label="Done"
                                                        icon={<Lock size={16} color="#fff" />}
                                                        clickHandler={() => {
                                                            markEnrollementStepAsFinished();
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={Style.mfaEnabled}>
                                    <div className="flex w-100 justify-content-between align-items-baseline">
                                        <p className={Style.mfaDescription}>
                                            Your account is protected with two-factor authentication.
                                        </p>
                                        {
                                            !mfaStatus.enforced &&
                                            <Button
                                                btnClass="d_btn d_btn_primary text-sm-400 font-family-imp"
                                                cssClass={Style.securityBnt}
                                                label="Disable MFA ?"
                                                clickHandler={() => { setShowDisableMFa(true); pinCodeRef?.current?.focus(); }}
                                            />
                                        }

                                    </div>
                                    {
                                        !mfaStatus.enforced && showDisableMFa &&
                                        (
                                            <div className={Style.mfaDisableSection}>
                                                <div className={`${Style.codeFieldWrapper}`}>
                                                    <PinInput
                                                        ref={pinCodeRef}
                                                        length={6}
                                                        type="numeric"
                                                        inputMode="number"
                                                        inputStyle={{ borderColor: '#808D9E', borderRadius: '4px', color: '#342c82' }}
                                                        inputFocusStyle={{ borderColor: '#342c82' }}
                                                        autoSelect={true}
                                                        focus={true}
                                                        onComplete={(val, idx) => { setMfaCode(val) }}
                                                        onChange={(val, idx) => { handleCodeChanged(val) }}
                                                    />
                                                    {
                                                        isValidCode &&
                                                        (
                                                            <Button
                                                                btnClass={`d_btn d_btn_danger text-sm-400 font-family-imp`}
                                                                cssClass={Style.btnMfa}
                                                                label="Disable MFA"
                                                                showLoading={mfaEnrollment.loading}
                                                                isDisabled={mfaEnrollment.loading || !isValidCode}
                                                                clickHandler={handleMfaDisable}
                                                            />
                                                        )
                                                    }
                                                </div>
                                                <div className="flex justify-content-center align-items-center">
                                                    {mfaError && <p className={Style.errorMessage}>{mfaError}</p>}
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className={Style.mainContainer}>
                <p className={Style.label}>Change Password</p>
                <div className="w-100">
                    <div className={Style.passwordFieldWrapper}>
                        <Input
                            inputwd="w-50 me-3"
                            icon={true}
                            dir={true}
                            placeholder="Current Password"
                            type="password"
                            handleChange={setcurrentPassword}
                            label="Current Password"
                            cssClass={Style.inputField}
                            iconClass={Style.icon}
                            errorMessage={formErrors.password}
                        />
                        <p className={Style.forgotPasswordLink} onClick={handleForgot}>Forgotten your password?</p>
                    </div>
                    <div className={Style.passwordFieldWrapper}>
                        <Input
                            inputwd="w-50 me-3"
                            icon={true}
                            dir={true}
                            placeholder="New Password"
                            type="password"
                            handleChange={handleChange}
                            label="New Password"
                            cssClass={Style.inputField}
                            iconClass={Style.icon}
                            labelClass={Style.labelBold}
                            errorMessage={formErrors.newPassword}
                        />
                    </div>
                    <div>
                        <Input
                            inputwd="w-50 me-3"
                            icon={true}
                            dir={true}
                            placeholder="Confirm Password"
                            type="password"
                            handleChange={setConfirmPassword}
                            label="Confirm Password"
                            cssClass={Style.inputField}
                            iconClass={Style.icon}
                            labelClass={Style.labelBold}
                            errorMessage={formErrors.confirmPassword}
                        />
                    </div>
                    <p className={Style.errorMessage}>{passwordError}</p>
                </div>
                <div className={Style.validationStrength}>
                    <ul>
                        <li style={{ color: password.length === 0 ? '#383838' : validationResults.length ? '#107C10' : '#B10E1C' }}>
                            <div className='d-flex justify-content-start align-items-center'>
                                <img src={password.length === 0 ? Checkmark : validationResults.length ? CheckmarkGreen : Dismiss} alt="" />
                                <p className='mb-0 ml-3'>At least 8 characters</p>
                            </div>
                        </li>
                        <li style={{ color: password.length === 0 ? '#383838' : validationResults.mixLettersNumbers ? '#107C10' : '#B10E1C' }}>
                            <div className='d-flex justify-content-start align-items-center'>
                                <img src={password.length === 0 ? Checkmark : validationResults.mixLettersNumbers ? CheckmarkGreen : Dismiss} alt="" />
                                <p className='mb-0 ml-3'>Mix of letter and numbers</p>
                            </div>
                        </li>
                        <li style={{ color: password.length === 0 ? '#383838' : validationResults.specialCharacter ? '#107C10' : '#B10E1C' }}>
                            <div className='d-flex justify-content-start align-items-center'>
                                <img src={password.length === 0 ? Checkmark : validationResults.specialCharacter ? CheckmarkGreen : Dismiss} alt="" />
                                <p className='mb-0 ml-3'>At least 1 special character</p>
                            </div>
                        </li>
                        <li style={{ color: password.length === 0 ? '#383838' : validationResults.mixCase ? '#107C10' : '#B10E1C' }}>
                            <div className='d-flex justify-content-start align-items-center'>
                                <img src={password.length === 0 ? Checkmark : validationResults.mixCase ? CheckmarkGreen : Dismiss} alt="" />
                                <p className='mb-0 ml-3'>At least one lower and one upper case letter</p>
                            </div>
                        </li>
                    </ul>
                </div>
                <div className={Style.btnWrapper}>
                    <Button btnClass="d_btn d_btn_primary text-sm-400 font-family-imp" cssClass={Style.securityBnt} label="Change password" showLoading={loading} clickHandler={handleUpdatePassword} />
                </div>
            </div>
        </div>
    );
}

export default Security;